import { create } from '@mangovoice/apisauce'
import type { ApisauceInstance } from '@mangovoice/apisauce'

import { setAuthInterceptors } from './interceptors/authInterceptor'
import { setRateLimitInterceptors } from './interceptors/rateLimitInterceptor'
import { createTransportAdapter } from './transport/createTransportAdapter'
import type { HttpTransport } from './transport/types'
import type { AuthInterceptorConfig, RateLimitInterceptorConfig } from './types'

export interface CreateAuthenticatedClientOptions {
  baseURL: string
  appName: string
  appVersion: string
  auth: AuthInterceptorConfig
  rateLimit?: RateLimitInterceptorConfig
  timeout?: number
}

export interface AuthenticatedClient {
  instance: ApisauceInstance
  transport: HttpTransport
}

/**
 * Creates an apisauce instance pre-configured with standard headers,
 * FormData transform, and auth + rate-limit interceptors.
 */
export function createAuthenticatedClient(
  options: CreateAuthenticatedClientOptions,
): AuthenticatedClient {
  const {
    baseURL,
    appName,
    appVersion,
    auth,
    rateLimit,
    timeout = 30_000,
  } = options

  const instance = create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-mango-app': appName,
      'x-mango-app-version': appVersion,
    },
  })

  instance.addRequestTransform((request) => {
    if (request.data instanceof FormData) {
      delete (request.headers as Record<string, unknown>)?.['Content-Type']
    }
  })

  setAuthInterceptors([instance], auth)

  if (rateLimit) {
    setRateLimitInterceptors([instance], rateLimit)
  }

  const transport = createTransportAdapter(instance)

  return { instance, transport }
}
